import { Types, toGeolocation } from "@citychallenge/common";
import { observable, computed, action } from "mobx";
import { convertMetersToKm } from "@citychallenge/common";

import {
  GameEntity,
  GameEntityGameTypeEnum,
  GeoPointDto,
  PurchaseEntity,
  ThemeEntity,
  UserEntity,
  CityEntity,
} from "@citychallenge/api-public";
import { Translation, TransTokens } from "../i18n";
import { Environment } from "../environment";
import { Preference } from "../services";
import { GameReleaseModel } from "./game-release.model";
import { GameSessionModel } from "../player/game-session/game-session.model";
import dayjs from "dayjs";

const tokens = TransTokens.create({
  mysteriousLocation: "Mysterious location",
  owned: "Owned",
  missing: "Missing",
  free: "Free",
  numChallenges: "{{numChallenges}} challenges",
  trivia: "Trivia",
  story: "Story",
  minigame: "Minigames",
});

interface Product {
  localizedPrice?: string;
  currency: string;
}

export class GameModel implements GameEntity {
  public entityType = "GameModel";

  @observable
  public id!: string;

  @observable
  public name!: string;

  @observable
  public price?: number;

  @observable
  public coordinates?: GeoPointDto;

  @observable
  public image?: string;

  @observable
  public description?: string;

  @observable
  public energyPoints?: number;

  @observable
  public hideLeaderboard?: boolean;
  @observable
  public hideReviews?: boolean;

  @observable
  public sku?: string;

  @observable
  public gameType!: GameEntityGameTypeEnum;

  @computed
  public get derivedSku() {
    if (!this.sku) return `${Environment.identifier}.${this.id}`.toLowerCase();
    else return this.sku;
  }

  @observable
  public created?: string;

  @observable
  public updated?: string;

  @observable
  public release!: GameReleaseModel;

  @observable
  public published?: boolean;

  @observable
  public distanceInMeters?: number;

  @observable
  public batteryNeeded?: number;

  @observable
  public timeInMinutes?: number;

  @observable
  public numChallenges?: number;

  @observable
  public address?: string;

  @observable
  public hideUntil?: string;

  @computed
  public get isVisible() {
    // Always show if user has a session
    if (this.activeSession) return true;

    // Legacy hidden switch
    if (this.hidden) return false;

    // Hide game from overview if it has a hide switch
    // and time is has not expired yet
    if (this.hideUntil && dayjs(this.hideUntil) > dayjs()) return false;

    return true;
  }

  @observable
  public product?: Product;

  @observable
  public purchases: PurchaseEntity[];

  @action
  public setPurchases = (purchases: PurchaseEntity[]) => {
    this.purchases = purchases;
  };

  @observable
  public reviewCount?: number;

  @observable
  public reviewRating?: number;

  @observable
  public playedCount?: number;

  @observable
  public creator?: UserEntity;

  @observable
  public theme?: ThemeEntity;

  @observable
  public hidden?: boolean;

  @observable
  public footerText?: string;

  @computed
  public get state() {
    if (this.activeSession?.completionPercentage === 100) return "completed";
    if (this.activeSession) return "started";
    return "default";
  }

  @computed
  public get isUnlocked() {
    return this._hasOpened.value || this.state !== "default";
  }

  public setHasOpenend(value: boolean) {
    this._hasOpened.setValue(value);
  }

  @action
  public setProduct(product?: Product) {
    this.product = product;
  }

  @observable
  public productError?: Error;

  // Note: this is the raw CityEntity
  public baseScenario: undefined;
  public releases: undefined;

  constructor(
    entity: GameEntity,
    private _hasOpened: Preference<boolean>,
    public city?: CityEntity,
    purchases?: PurchaseEntity[]
  ) {
    Object.assign(this, entity);

    if (entity.release) {
      this.release = new GameReleaseModel(entity.release, this);
    }

    this.purchases = purchases || entity.purchases || [];
  }

  @computed
  public get addressText(): string | Translation | undefined {
    return this.address ? this.address : tokens.mysteriousLocation;
  }

  @computed
  public get startingLocation(): Types["GeoLocation"] | undefined {
    if (!this.coordinates) return undefined;

    return toGeolocation(this.coordinates.coordinates as [number, number]);
  }

  @computed
  public get isPurchased(): boolean {
    return !!this.purchases.length;
  }

  @computed
  public get isGameOwned(): boolean {
    return this.isFree || this.isPurchased;
  }

  @computed
  public get priceText(): string | Translation {
    if (this.isPurchased) {
      return tokens.owned;
    }
    if (this.price && !this.product) {
      return tokens.missing;
    }
    if (!this.product || !this.price) {
      return tokens.free;
    } else {
      const price =
        this.product.localizedPrice ||
        `${this.product.currency.replace("EUR", "€")} ${this.price}`;

      return `${price.replace(",", ".")}`;
    }
  }

  @computed
  public get isFree(): boolean {
    return !this.price;
  }

  @computed
  public get ratingText(): string | Translation | undefined {
    return this.reviewRating
      ? `${this.reviewRating.toPrecision(2)}`
      : undefined;
  }

  @computed
  public get durationText(): string | undefined {
    return this.timeInMinutes ? `${this.timeInMinutes} min` : undefined;
  }

  @computed
  public get distanceText(): string | undefined {
    return this.distanceInMeters
      ? convertMetersToKm(this.distanceInMeters)
      : undefined;
  }

  @computed
  public get themeText(): string | undefined {
    return this.theme ? this.theme.name : undefined;
  }

  @computed
  public get gameTypeText(): Translation | undefined {
    return (tokens as { [key: string]: Translation })[this.gameType || ""];
  }

  @computed
  public get challengesText(): string | Translation | undefined {
    return this.numChallenges ? tokens.numChallenges : undefined;
  }

  @observable
  public activeSession?: GameSessionModel = undefined;
}
